import { ReactComponent as OutlineChevronRight } from "assets/svg/outline-chevron-right.svg";
import styles from "./MarketPlaceBreadcrumbs.module.scss";
import { useMemo } from "react";
import { Helmet } from "react-helmet";
import { BreadcrumbList, WithContext } from "schema-dts";

interface MarkerPlaceBreadcrumbsProps {
  specialtyName: string;
  stateName: string;
  targetPathname: string;
}

const MarketPlaceBreadcrumbs = ({
  specialtyName,
  stateName,
  targetPathname,
}: MarkerPlaceBreadcrumbsProps) => {
  const isSpecialtySpecified = useMemo(
    () => specialtyName !== "Clinician",
    [specialtyName],
  );

  const specialtyLink = useMemo(
    () => `/${specialtyName.toLowerCase()}s`,
    [specialtyName],
  );

  const structuredData = useMemo(() => {
    const { origin } = window.location;

    const items = [
      { name: "Find professional", url: `${origin}/professional-search` },
    ];

    if (isSpecialtySpecified) {
      items.push({
        name: `${specialtyName}s`,
        url: `${origin}${specialtyLink}`,
      });
    }

    if (stateName) {
      items.push({ name: stateName, url: `${origin}${targetPathname}` });
    }

    const itemListElement: BreadcrumbList["itemListElement"] = items.map(
      ({ name, url }, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name,
        item: url,
      }),
    );

    const structuredData: WithContext<BreadcrumbList> = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement,
    };

    return structuredData;
  }, [
    isSpecialtySpecified,
    specialtyLink,
    specialtyName,
    stateName,
    targetPathname,
  ]);

  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>
      <div className={styles["breadcrumbs"]}>
        <a href="/professional-search">Find therapist</a>
        {isSpecialtySpecified && (
          <>
            <OutlineChevronRight />
            <a href={specialtyLink}>{specialtyName}s</a>
          </>
        )}
        {stateName && (
          <>
            <OutlineChevronRight />
            <a href={targetPathname}>{stateName}</a>
          </>
        )}
      </div>
    </>
  );
};

export default MarketPlaceBreadcrumbs;
